import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import {
  Box,
  Button,
  CircularProgress,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { useState, type MouseEvent } from "react";
import { useDispatch } from "react-redux";
import { sendMeetingReminder } from "../../apis/invitations.api";

const SEND_REMINDER_LABEL = "Send reminder";
const SENDING_REMINDER_LABEL = "Sending reminder...";
const REMINDER_COLOR = "#EA580C";
const REMINDER_COLOR_HOVER = "#C2410C";

function ReminderIcon({ sending, size }: { sending: boolean; size: number }) {
  if (sending) {
    return (
      <CircularProgress size={size} thickness={5} sx={{ color: REMINDER_COLOR }} />
    );
  }

  return (
    <NotificationsActiveOutlinedIcon sx={{ fontSize: size + 2, color: REMINDER_COLOR }} />
  );
}

function SendMeetingReminderButton({
  ambassadorInvitationId,
  variant = "inline",
}: {
  ambassadorInvitationId?: string;
  variant?: "inline" | "popover";
}) {
  const dispatch = useDispatch();
  const [sending, setSending] = useState(false);
  const disabled = !ambassadorInvitationId || sending;
  const label = sending ? SENDING_REMINDER_LABEL : SEND_REMINDER_LABEL;

  const handleSendReminder = async (e?: MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (disabled) return;

    setSending(true);
    try {
      await dispatch(sendMeetingReminder(ambassadorInvitationId));
    } finally {
      setSending(false);
    }
  };

  if (variant === "popover") {
    return (
      <ListItemButton
        onClick={handleSendReminder}
        disabled={disabled}
        sx={{
          py: 1.1,
          px: 1.5,
          borderTop: "1px solid #F1F5F9",
          "&:hover": { bgcolor: "#FFF7ED" },
          "&.Mui-disabled": { opacity: 0.65 },
        }}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>
          <ReminderIcon sending={sending} size={18} />
        </ListItemIcon>
        <ListItemText
          primary={label}
          secondary="Sent to ambassador only"
          primaryTypographyProps={{
            fontSize: 13.5,
            fontWeight: 600,
            color: REMINDER_COLOR,
          }}
          secondaryTypographyProps={{
            fontSize: 11.5,
            color: "#94A3B8",
            mt: 0.25,
          }}
        />
        {!sending && (
          <ChevronRightRoundedIcon sx={{ fontSize: 20, color: "#FDBA74" }} />
        )}
      </ListItemButton>
    );
  }

  return (
    <Box mt={0.5}>
      <Button
        size="small"
        variant="text"
        onClick={handleSendReminder}
        disabled={disabled}
        startIcon={<ReminderIcon sending={sending} size={10} />}
        sx={{
          p: 0,
          minWidth: 0,
          minHeight: 0,
          fontSize: 10,
          fontWeight: 600,
          lineHeight: 1.4,
          color: REMINDER_COLOR,
          textTransform: "none",
          "& .MuiButton-startIcon": {
            mr: 0.375,
            ml: 0,
          },
          "&:hover": {
            bgcolor: "transparent",
            color: REMINDER_COLOR_HOVER,
          },
          "&.Mui-disabled": {
            color: "#94A3B8",
          },
        }}
      >
        {label}
      </Button>
    </Box>
  );
}

export { SendMeetingReminderButton };
